import { type PropertiesConfig } from '@blaze-shared/elements-domain';
import { COLUMN_ID_OR_NAME } from '@blaze-shared/schema-domain';

import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { type AvailabilityPropsModel } from './availability.model';

export function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('availability', [
    epc.section('content', 'Content', [
      epc.subsections.dataSubsection(
        'data.?key:value.enabled',
        '',
        [],
        [
          epc.utils.disabledField(
            epc.fields.selectDataSourceField({
              key: 'value',
              label: 'Value',
              sources: 'list',
              bindings: [],
            }),
            (model: AvailabilityPropsModel) =>
              ['form-group', 'wizard'].includes(model.content.origin)
                ? 'Data source cannot be modified in an auto-generated form.'
                : undefined,
          ),

          ...epc.utils.conditionalFields(
            [
              epc.fields.selectFieldFromSchema(
                'content.input.startDateColumn',
                '* Start date',
                'data.?key:value.dataSource',
                {
                  valueField: COLUMN_ID_OR_NAME,
                  hideLabel: false,
                  optionsFilter: epc.utils.columnDataTypeFilter('Date'),
                },
              ),
              epc.fields.selectFieldFromSchema(
                'content.input.endDateColumn',
                '* End date',
                'data.?key:value.dataSource',
                {
                  valueField: COLUMN_ID_OR_NAME,
                  hideLabel: false,
                  optionsFilter: epc.utils.columnDataTypeFilter('Date'),
                },
              ),
              epc.fields.selectFieldFromSchema(
                'content.input.dayOfWeekColumn',
                '* Day of the week',
                'data.?key:value.dataSource',
                {
                  valueField: COLUMN_ID_OR_NAME,
                  hideLabel: false,
                  optionsFilter: epc.utils.columnDataTypeFilter('Number', ['Number']),
                },
              ),
              epc.fields.selectFieldFromSchema(
                'content.input.startTimeColumn',
                '* Start time',
                'data.?key:value.dataSource',
                {
                  valueField: COLUMN_ID_OR_NAME,
                  hideLabel: false,
                  optionsFilter: epc.utils.columnDataTypeFilter('Time'),
                },
              ),
              epc.fields.selectFieldFromSchema(
                'content.input.endTimeColumn',
                '* End time',
                'data.?key:value.dataSource',
                {
                  valueField: COLUMN_ID_OR_NAME,
                  hideLabel: false,
                  optionsFilter: epc.utils.columnDataTypeFilter('Time'),
                },
              ),
            ],
            'data.?key:value.dataSource',
            (value) => !value,
          ),
        ],
        {
          enabledByDefault: true,
          subsectionOptions: { hideLabel: true },
        },
      ),
      epc.subsections.subsection('content', 'Options', [
        epc.fields.selectFieldFromList(
          'content.options.weekStart',
          'Week starts on',
          [
            { label: 'Monday', value: 'Monday' },
            { label: 'Sunday', value: 'Sunday' },
          ],
          { allowNone: false, labelWidth: '38%' },
        ),
        epc.utils.conditionalField(
          epc.fields.checksField('content.options.visibleDays', 'Visible days', {
            options: [
              { label: 'S', value: 'Sunday' },
              { label: 'M', value: 'Monday' },
              { label: 'T', value: 'Tuesday' },
              { label: 'W', value: 'Wednesday' },
              { label: 'T', value: 'Thursday' },
              { label: 'F', value: 'Friday' },
              { label: 'S', value: 'Saturday' },
            ],
            renderMode: 'separated',
            togglesContainerCssClass: 'justify-between',
            toggleCssClass: 'w-32',
          }),
          'content.options.weekStart',
          (weekStart) => weekStart === 'Monday',
        ),
        epc.utils.conditionalField(
          epc.fields.checksField('content.options.visibleDays', 'Visible days', {
            options: [
              { label: 'M', value: 'Monday' },
              { label: 'T', value: 'Tuesday' },
              { label: 'W', value: 'Wednesday' },
              { label: 'T', value: 'Thursday' },
              { label: 'F', value: 'Friday' },
              { label: 'S', value: 'Saturday' },
              { label: 'S', value: 'Sunday' },
            ],
            renderMode: 'separated',
            togglesContainerCssClass: 'justify-between',
            toggleCssClass: 'w-32',
          }),
          'content.options.weekStart',
          (weekStart) => weekStart === 'Sunday',
        ),

        epc.utils.separator('Defaults'),
        epc.fields.timeRangeField('content.options.time', 'Start time', {
          secondaryLabel: 'End time',
        }),
      ]),

      epc.subsections.visibilitySubsection(),
      epc.subsections.titleContentSubsection('content'),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),

        epc.subsections.subsection('style.general', 'Padding', [
          epc.fields.spacingAndAlignField('style.general.offset', {
            hasPadding: true,
          }),
        ]),

        epc.subsections.subsection('style.general', 'General', [
          epc.fields.colorField('style.general.accent', 'Accent', {
            allowNone: false,
            allowGradient: false,
          }),
          epc.fields.colorField('style.general.fill', 'Fill', {}),
          epc.fields.colorField('style.general.outline', 'Outline', { allowGradient: false }),
          epc.fields.sliderField('style.general.outlineWidth', 'Width', [
            { min: 0, max: 10, unit: 'px' },
          ]),
          epc.fields.radiusField('style.general.radius'),
          ...epc.fields.visualEffectField('style.general.visualEffect'),
        ]),
        epc.subsections.titleStyleSubsection({
          enabled: 'content.title.enabled',
          color: 'style.general.title',
          fontFamily: 'style.titleBlock.fontFamily',
          fontWeight: 'style.titleBlock.fontWeight',
          fontStyle: 'style.titleBlock.fontStyle',
          fontSize: 'style.titleBlock.fontSize',
          horizontalAlign: 'content.title.align',
          icon: 'content.title.icon',
          iconColor: 'content.title.iconColor',
          iconPosition: 'content.title.iconPosition',
          iconSize: 'style.titleBlock.iconSize',
        }),
      ],
      true,
    ),
  ]);
}
